import axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { formatSelectedServices } from '../utils/formatSelectedServices';
import {
	CHECKBOX_EXTENDED_FIELDS,
	EXTENDED_ONLY_FIELDS,
	pruneAdditionalServices,
	resolveAdCreativeType,
	showsExtendedPackageFields,
	visibleOrderData,
} from '../data/acOrderRules';
import { buildApiUrl } from '../utils/apiUrl';
import { checkClientVersion } from '../utils/clientVersion';
import { useAuth } from '../contexts/AuthContext';
import {
	formatUploadStatus,
	newSubmissionId,
	processStatusAt,
} from '../utils/submitProgress';
import { createSubmitFlow, describeSubmitFiles } from '../utils/submitFlow';
import {
	deleteMyDraft,
	fetchMyDraft,
	saveMyDraft,
} from '../api/formDraftApi';
import { acSwal, acAlert } from '../utils/acSwal';

import Footer from '../sections/pageSections/footer';
import FormContent from './subsections/formContent';

const AUTOSAVE_DEBOUNCE_MS = 2500;

const SalesForm = () => {
	const navigate = useNavigate();
	const methods = useForm();
	const { handleSubmit, setValue, getValues, watch, control, register } =
		methods;
	const [selectedLocation, setSelectedLocation] = useState('');
	const [selectedServices, setSelectedServices] = useState({});
	const [userDepartment, setUserDepartment] = useState('');
	const [sending, setSending] = useState(false);
	const [submitStatus, setSubmitStatus] = useState('');
	const [autosaveUi, setAutosaveUi] = useState('');
	const sendingLock = useRef(false);
	const submissionIdRef = useRef(null);
	const { id } = useParams();
	const { user, isLoggedIn, refreshToken, devMailMode } = useAuth();
	const packageChoice = watch('packageChoice');
	const previousPackageRef = useRef(packageChoice);

	useEffect(() => {
		const previous = previousPackageRef.current;
		previousPackageRef.current = packageChoice;
		if (previous === packageChoice) {
			return;
		}
		if (!showsExtendedPackageFields(packageChoice)) {
			EXTENDED_ONLY_FIELDS.forEach((field) => {
				setValue(
					field,
					CHECKBOX_EXTENDED_FIELDS.includes(field) ? false : '',
					{ shouldDirty: true }
				);
			});
		}
		setSelectedServices((current) =>
			pruneAdditionalServices(packageChoice, current)
		);
		const kept = pruneAdditionalServices(
			packageChoice,
			getValues('additionalServices') || {}
		);
		setValue('additionalServices', kept, { shouldDirty: true });
	}, [packageChoice, setValue, getValues]);

	const autosaveEnabledRef = useRef(false);
	const lastAutosavedJsonRef = useRef('');
	const autosaveDebounceRef = useRef(null);
	const autosaveUiClearRef = useRef(null);
	const autosaveAbortRef = useRef(null);
	const draftInitDoneRef = useRef(false);

	const buildDraftPayload = useCallback(
		(data) => {
			const pkg = data.packageChoice;
			const services = pruneAdditionalServices(pkg, selectedServices);
			const draftData = visibleOrderData({
				...data,
				additionalServices: services,
				servicesColumnList: formatSelectedServices(services),
			});
			delete draftData.companyProfile;
			delete draftData.submissionId;
			delete draftData.devMailMode;
			delete draftData.devMailTo;
			return draftData;
		},
		[selectedServices]
	);

	const buildDraftPayloadRef = useRef(buildDraftPayload);
	buildDraftPayloadRef.current = buildDraftPayload;

	const applyDraftServerData = useCallback(
		(savedData) => {
			if (!savedData || typeof savedData !== 'object') {
				return;
			}
			const cleaned = visibleOrderData(savedData) || {};
			Object.keys(cleaned).forEach((key) => {
				if (key === 'additionalServices') {
					return;
				}
				setValue(key, cleaned[key], { shouldDirty: true });
			});
			const creativeType = resolveAdCreativeType(cleaned);
			setValue('adCreativeType', creativeType || '', {
				shouldDirty: true,
			});
			if (
				cleaned.additionalServices &&
				typeof cleaned.additionalServices === 'object'
			) {
				setSelectedServices(cleaned.additionalServices);
			} else {
				setSelectedServices({});
			}
			if (savedData.selectedLocation) {
				setSelectedLocation(savedData.selectedLocation);
				setUserDepartment(savedData.selectedLocation);
			}
		},
		[setValue]
	);

	useEffect(() => {
		autosaveAbortRef.current = new AbortController();
		return () => {
			autosaveAbortRef.current?.abort();
		};
	}, []);

	useEffect(() => {
		if (!id) {
			return undefined;
		}
		let cancelled = false;
		const loadSavedFormData = async () => {
			try {
				const response = await axios.get(
					buildApiUrl(`form/recover/${id}`)
				);
				if (cancelled) {
					return;
				}
				applyDraftServerData(response.data);
			} catch (error) {
				console.error('Error loading saved form data:', error);
			}
		};
		loadSavedFormData();
		return () => {
			cancelled = true;
		};
	}, [id, applyDraftServerData]);

	useEffect(() => {
		if (!user?.department) {
			return;
		}
		setUserDepartment((prev) => prev || user.department);
		if (!getValues('selectedLocation')) {
			setSelectedLocation(user.department);
			setValue('selectedLocation', user.department);
		}
	}, [user, setValue, getValues]);

	useEffect(() => {
		if (!isLoggedIn) {
			draftInitDoneRef.current = false;
			autosaveEnabledRef.current = false;
			return undefined;
		}
		if (id) {
			autosaveEnabledRef.current = true;
			draftInitDoneRef.current = true;
			return undefined;
		}
		if (draftInitDoneRef.current) {
			return undefined;
		}

		let cancelled = false;
		autosaveEnabledRef.current = false;

		const finishEnableAutosave = () => {
			if (!cancelled) {
				autosaveEnabledRef.current = true;
			}
			draftInitDoneRef.current = true;
		};

		const scheduleSyncLastSavedAndEnableAutosave = () => {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					if (cancelled) {
						return;
					}
					try {
						lastAutosavedJsonRef.current = JSON.stringify(
							buildDraftPayloadRef.current(getValues())
						);
					} catch {
						/* ignore */
					}
					finishEnableAutosave();
				});
			});
		};

		const run = async () => {
			try {
				const data = await fetchMyDraft({
					signal: autosaveAbortRef.current?.signal,
				});
				if (cancelled) {
					return;
				}

				const draft = data?.draft;
				if (!draft?.data) {
					lastAutosavedJsonRef.current = '';
					finishEnableAutosave();
					return;
				}

				const savedAt = draft.savedAt
					? new Date(draft.savedAt).toLocaleString('nb-NO')
					: '';
				const sw = await acSwal({
					icon: 'question',
					title: 'Lagret utkast',
					html: `<p>Vi fant et påbegynt skjema${
						savedAt ? ` (lagret ${savedAt})` : ''
					}.</p><p>Vil du fortsette der du slapp, eller starte på nytt?</p>`,
					showDenyButton: true,
					confirmButtonText: 'Fortsett på utkast',
					denyButtonText: 'Slett utkast og start blankt',
					allowOutsideClick: false,
				});

				if (cancelled) {
					return;
				}

				if (sw.isDenied) {
					try {
						await deleteMyDraft({
							signal: autosaveAbortRef.current?.signal,
						});
					} catch (e) {
						console.error(e);
						await acSwal({
							icon: 'error',
							title: 'Kunne ikke slette utkast',
							text: 'Prøv igjen eller kontakt support.',
						});
					}
					lastAutosavedJsonRef.current = '';
					finishEnableAutosave();
					return;
				}

				if (sw.isConfirmed) {
					applyDraftServerData(draft.data);
					scheduleSyncLastSavedAndEnableAutosave();
					return;
				}

				lastAutosavedJsonRef.current = '';
				finishEnableAutosave();
			} catch (e) {
				if (axios.isAxiosError(e) && e.code === 'ERR_CANCELED') {
					return;
				}
				console.error('[SalesForm] draft/me ved mount:', e);
				finishEnableAutosave();
			}
		};

		void run();

		return () => {
			cancelled = true;
		};
	}, [isLoggedIn, id, applyDraftServerData, getValues]);

	const formData = watch();

	const persistDraft = useCallback(async () => {
		if (!isLoggedIn || sendingLock.current) {
			return;
		}
		let snapshot;
		try {
			snapshot = getValues();
		} catch {
			return;
		}
		const payload = buildDraftPayload(snapshot);
		let json;
		try {
			json = JSON.stringify(payload);
		} catch {
			return;
		}
		if (json === lastAutosavedJsonRef.current) {
			return;
		}
		try {
			setAutosaveUi('Lagrer utkast…');
			await saveMyDraft(payload, {
				signal: autosaveAbortRef.current?.signal,
			});
			lastAutosavedJsonRef.current = json;
			setAutosaveUi('Utkast lagret');
			clearTimeout(autosaveUiClearRef.current);
			autosaveUiClearRef.current = setTimeout(() => {
				setAutosaveUi((u) => (u === 'Utkast lagret' ? '' : u));
			}, 2000);
		} catch (e) {
			if (axios.isAxiosError(e) && e.code === 'ERR_CANCELED') {
				return;
			}
			console.error('[SalesForm] autosave:', e);
			setAutosaveUi('Utkast ikke lagret');
		}
	}, [isLoggedIn, getValues, buildDraftPayload]);

	useEffect(() => {
		if (!isLoggedIn) {
			return undefined;
		}
		const sub = watch(() => {
			if (!autosaveEnabledRef.current) {
				return;
			}
			clearTimeout(autosaveDebounceRef.current);
			autosaveDebounceRef.current = setTimeout(() => {
				void persistDraft();
			}, AUTOSAVE_DEBOUNCE_MS);
		});
		return () => {
			sub.unsubscribe();
			clearTimeout(autosaveDebounceRef.current);
			clearTimeout(autosaveUiClearRef.current);
		};
	}, [watch, isLoggedIn, persistDraft]);

	useEffect(() => {
		if (!isLoggedIn || !autosaveEnabledRef.current) {
			return undefined;
		}
		clearTimeout(autosaveDebounceRef.current);
		autosaveDebounceRef.current = setTimeout(() => {
			void persistDraft();
		}, AUTOSAVE_DEBOUNCE_MS);
		return () => {
			clearTimeout(autosaveDebounceRef.current);
		};
	}, [selectedServices, isLoggedIn, persistDraft]);

	const handleLocationSelect = (location) => {
		setSelectedLocation(location);
		setValue('selectedLocation', location);
	};

	const storeError = (errorData) => {
		const errors = localStorage.getItem('frontendErrors');
		const parsedErrors = errors ? JSON.parse(errors) : [];
		parsedErrors.push(errorData);
		localStorage.setItem('frontendErrors', JSON.stringify(parsedErrors));
	};

	const logErrorToBackend = (errorData) => {
		fetch(buildApiUrl('log/frontend-error'), {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(errorData),
		}).catch((err) =>
			console.error('Error logging frontend error to backend:', err)
		);
	};

	const onSubmit = async (data) => {
		if (sendingLock.current) {
			return;
		}
		if (!(await checkClientVersion())) {
			return;
		}
		sendingLock.current = true;
		if (!submissionIdRef.current) {
			submissionIdRef.current = newSubmissionId();
		}
		const submissionId = submissionIdRef.current;
		const flow = createSubmitFlow(submissionId);

		const files = getValues('companyProfile');
		const hasFiles = Boolean(files && files.length);
		const fileInfo = describeSubmitFiles(files);
		flow.ping(
			'klikk',
			`${fileInfo.count} filer, ${fileInfo.total} bytes (${fileInfo.names})`
		);
		setSending(true);
		setSubmitStatus(formatUploadStatus(hasFiles, 0, 0));
		let processTick = null;
		const startProcessTicks = () => {
			if (processTick) {
				return;
			}
			const processStartedAt = Date.now();
			setSubmitStatus(processStatusAt(0));
			processTick = window.setInterval(() => {
				setSubmitStatus(processStatusAt(Date.now() - processStartedAt));
			}, 400);
		};
		const stopSending = () => {
			if (processTick) {
				window.clearInterval(processTick);
			}
			sendingLock.current = false;
			setSending(false);
			setSubmitStatus('');
		};

		data.servicesColumnList = formatSelectedServices(
			pruneAdditionalServices(data.packageChoice, selectedServices)
		);

		const submissionData = visibleOrderData({
			...data,
			additionalServices: pruneAdditionalServices(
				data.packageChoice,
				selectedServices
			),
			submissionId: submissionIdRef.current,
		});

		if (devMailMode) {
			submissionData.devMailMode = true;
			submissionData.devMailTo = user?.email || data.ordererEmail || '';
		}

		try {
			flow.mark('refreshToken start');
			await refreshToken();
			flow.ping('refreshToken ferdig');

			const formDataPayload = new FormData();
			formDataPayload.append('formData', JSON.stringify(submissionData));

			if (hasFiles) {
				files.forEach((file) =>
					formDataPayload.append('companyProfile', file)
				);
			}
			flow.ping(
				'FormData klar, axios.post start',
				`${fileInfo.count} filer, ${fileInfo.total} bytes`
			);

			await axios.post(
				`${buildApiUrl('form/submit-form')}?sid=${encodeURIComponent(
					submissionId
				)}&clientT0=${flow.t0}&filesBytes=${fileInfo.total}`,
				formDataPayload,
				{
					onUploadProgress: (event) => {
						if (event.lengthComputable && event.total > 0) {
							flow.uploadProgress(event.loaded, event.total);
							setSubmitStatus(
								formatUploadStatus(
									hasFiles,
									event.loaded,
									event.total
								)
							);
							if (event.loaded >= event.total) {
								flow.mark('nettleser ferdig sendt, venter på server');
								startProcessTicks();
							}
							return;
						}
						setSubmitStatus(formatUploadStatus(hasFiles, 0, 0));
					},
				}
			);
			flow.ping('axios svar mottatt');
			startProcessTicks();

			try {
				await deleteMyDraft();
			} catch (draftError) {
				console.error('Could not delete draft after submit:', draftError);
			}

			flow.ping('klient ferdig, navigerer til kvittering');
			navigate('/review');
			stopSending();
		} catch (error) {
			flow.ping(
				'klient feil',
				error && error.message ? String(error.message).slice(0, 200) : ''
			);
			console.error('Form submission error:', error);

			if (
				axios.isAxiosError(error) &&
				error.response?.status === 400 &&
				Array.isArray(error.response.data?.details)
			) {
				const lines = error.response.data.details
					.map((item) => `${item.field}: ${item.message}`)
					.join('\n');
				await acAlert(
					`${error.response.data.error || 'Skjemaet er ugyldig'}\n${lines}`
				);
				stopSending();
				return;
			}

			let errorData;
			if (axios.isAxiosError(error) && error.response) {
				const { status, data: resData } = error.response;
				const bodySnippet =
					typeof resData === 'string'
						? resData
						: resData?.error ||
						resData?.message ||
						(typeof resData === 'object'
							? JSON.stringify(resData)
							: String(resData));
				errorData = {
					message: `Skjemainnsending feilet (${status}): ${bodySnippet}`,
					source: 'Form submission',
					lineno: 0,
					colno: 0,
					error: bodySnippet,
					status,
					userAgent: navigator.userAgent,
					timestamp: new Date().toISOString(),
				};
			} else if (axios.isAxiosError(error)) {
				errorData = {
					message: `Skjemainnsending: ${error.message}`,
					source: 'Form submission',
					lineno: 0,
					colno: 0,
					error: error.message,
					userAgent: navigator.userAgent,
					timestamp: new Date().toISOString(),
				};
			} else {
				errorData = {
					message: error.message,
					source: window.location.href,
					lineno: error.lineNumber || 0,
					colno: error.columnNumber || 0,
					error: error.stack || null,
					userAgent: navigator.userAgent,
					timestamp: new Date().toISOString(),
				};
			}

			storeError(errorData);
			logErrorToBackend(errorData);
			stopSending();

			try {
				await saveMyDraft(buildDraftPayload(data));
			} catch (draftError) {
				console.error('Could not save draft after submit error:', draftError);
			}

			await acAlert(
				'Noe gikk galt ved innsending. Utkastet er lagret på kontoen din, så du kan fortsette der du slapp etter innlogging.'
			);
		}
	};

	const handleChange = (name, value) => {
		setValue(name, value);
	};

	return (
		<FormProvider {...methods}>
			<form
				className={`w-2/3 mx-auto${sending ? ' is-submitting' : ''}`}
				onSubmit={handleSubmit(onSubmit, async (errors) => {
					if (errors.packageChoice) {
						await acAlert(
							'Velg en bestillingspakke før du sender inn.'
						);
						return;
					}
					if (errors.adCreativeType) {
						await acAlert(
							'Velg stillbildevideo eller animert annonse.'
						);
						return;
					}
					await acAlert('Fyll ut alle påkrevde felt.');
				})}
				aria-busy={sending}
			>
				<FormContent
					control={control}
					register={register}
					handleChange={handleChange}
					formData={formData}
					setValue={setValue}
					selectedLocation={selectedLocation}
					handleLocationSelect={handleLocationSelect}
					selectedServices={selectedServices}
					setSelectedServices={setSelectedServices}
					getValues={getValues}
					preselectedLocation={userDepartment}
					sending={sending}
					submitStatus={submitStatus}
					autosaveUi={autosaveUi}
				/>
				<input type='hidden' {...register('selectedLocation')} />
				<Footer />
			</form>
		</FormProvider>
	);
};

export default SalesForm;
